import FeatureLocked from "@/components/feature/FeatureLocked";
import { useEntitlements } from "@/contexts/EntitlementsContext";
import { useEffect, useMemo, useState } from "react";
import AppLayout from "@/components/feature/AppLayout";
import { useWorkspace } from "@/contexts/WorkspaceContext";
import { fetchDocsSiteSettings } from "@/api/docsSiteApi";
import DocsHostingPanel from "./components/DocsHostingPanel";
import DocsPagesPanel from "./components/DocsPagesPanel";
import { StatChip, StatusBadge } from "./components/docsSiteUi";
import { publicPageUrl } from "./docsSiteConstants";

export default function DocsSitePage() {
  const { hasFeature } = useEntitlements();
  if (!hasFeature("knowledge_base")) return <FeatureLocked feature="knowledge_base" />;

  const { activeWorkspace } = useWorkspace();
  const [activeTab, setActiveTab] = useState("hosting");
  const [published, setPublished] = useState(false);
  const [settings, setSettings] = useState(null);
  const [pages, setPages] = useState([]);
  const [loadError, setLoadError] = useState(null);

  const workspaceSlug = activeWorkspace?.slug ?? "";
  const workspaceName = activeWorkspace?.name ?? "Workspace";
  const slug = workspaceSlug || "your-workspace";

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const data = await fetchDocsSiteSettings();
        if (!cancelled) {
          setSettings(data);
          setPublished(Boolean(data?.isPublished));
        }
      } catch (e) {
        if (!cancelled) setLoadError(e.message || "Failed to load docs site settings");
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [activeWorkspace?.id]);

  const publishedCount = useMemo(() => pages.filter((p) => p.status === "published").length, [pages]);

  const primaryUrl = publicPageUrl(settings) || settings?.sentrodeskBaseUrl || "";

  const tabs = [
    { id: "hosting", label: "Hosting & domain", icon: "ri-global-line" },
    { id: "pages", label: "Pages", icon: "ri-file-text-line", count: pages.length },
  ];

  return (
    <AppLayout>
      <div className="ui-page scrollbar-thin h-full overflow-y-auto">
        <header className="ui-shell sticky top-0 z-10">
          <div className="flex flex-col gap-6 xl:flex-row xl:items-start xl:justify-between">
            <div className="min-w-0 flex-1">
              <p className="text-xs font-semibold uppercase tracking-widest text-teal-700 dark:text-teal-400">
                Documentation
              </p>
              <h1 className="mt-1 text-2xl font-semibold tracking-tight text-slate-900 sm:text-3xl dark:text-slate-100">
                Docs Site
              </h1>
              <p className="mt-2 max-w-2xl text-sm leading-relaxed text-slate-500">
                Publish a branded documentation site for{" "}
                <span className="font-medium text-slate-700 dark:text-slate-300">{workspaceName}</span>. Host on
                SentroDesk or connect your own domain.
              </p>
              {loadError && <p className="mt-2 text-sm text-red-600 dark:text-red-400">{loadError}</p>}
            </div>

            <div className="flex flex-wrap gap-2 xl:max-w-md xl:justify-end">
              <StatChip label="Workspace" value={slug} tone="brand" />
              <StatChip label="Published pages" value={String(publishedCount)} tone={publishedCount ? "success" : "neutral"} />
              <StatChip label="Site status" value={published ? "Online" : "Offline"} tone={published ? "success" : "warn"} />
            </div>
          </div>

          <div className="mt-6 flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <nav className="flex flex-wrap gap-2" aria-label="Docs site sections">
              {tabs.map((tab) => {
                const active = activeTab === tab.id;
                return (
                  <button
                    key={tab.id}
                    type="button"
                    onClick={() => setActiveTab(tab.id)}
                    className={`inline-flex items-center gap-2 rounded-xl px-4 py-2 text-sm font-medium transition-all ${
                      active ? "ui-pill-active" : "ui-pill-idle"
                    }`}
                  >
                    <i className={`${tab.icon} text-base`} aria-hidden />
                    {tab.label}
                    {tab.count != null && (
                      <span
                        className={`rounded-md px-1.5 py-0.5 text-[10px] font-bold ${
                          active ? "bg-teal-100 text-teal-800 dark:bg-teal-900/60 dark:text-teal-200" : "bg-slate-200/80 text-slate-600 dark:bg-slate-700 dark:text-slate-300"
                        }`}
                      >
                        {tab.count}
                      </span>
                    )}
                  </button>
                );
              })}
            </nav>

            <div className="flex items-center gap-3 rounded-xl border border-slate-200/80 bg-slate-50/80 px-4 py-2.5 ring-1 ring-slate-100 dark:border-slate-700 dark:bg-slate-900/50 dark:ring-slate-800">
              <div className="min-w-0 flex-1">
                <p className="text-[10px] font-bold uppercase tracking-wider text-slate-400">Primary URL</p>
                <p className="truncate font-mono text-xs text-slate-700 dark:text-slate-300">{primaryUrl}</p>
              </div>
              <StatusBadge status={published ? "online" : "offline"} size="compact" />
            </div>
          </div>
        </header>

        <div className="mx-auto w-full max-w-6xl px-6 py-8 sm:px-8">
          {activeTab === "hosting" && (
            <DocsHostingPanel
              workspaceSlug={workspaceSlug}
              workspaceName={workspaceName}
              published={published}
              onPublishedChange={setPublished}
              settings={settings}
              onSettingsChange={setSettings}
            />
          )}
          {activeTab === "pages" && (
            <DocsPagesPanel settings={settings} published={published} onPagesChange={setPages} />
          )}
        </div>
      </div>
    </AppLayout>
  );
}
